import type { ApiChecklistItem, ApiChecklistSection } from "./api.js";

/** Where an item lives, using this file's existing (sectionIdx, subgroupId|null) convention. */
export type ItemAddress = { sectionIdx: number; subgroupId: string | null };

export type LocatedItem = { item: ApiChecklistItem; address: ItemAddress };

function readContainerItems(
  sections: ApiChecklistSection[],
  sectionIdx: number,
  subgroupId: string | null
): ApiChecklistItem[] {
  const section = sections[sectionIdx];
  if (!section) return [];
  if (subgroupId === null) return section.items;
  return section.subgroups?.find((sg) => sg.id === subgroupId)?.items ?? [];
}

function writeContainerItems(
  sections: ApiChecklistSection[],
  sectionIdx: number,
  subgroupId: string | null,
  nextItems: ApiChecklistItem[]
): ApiChecklistSection[] {
  return sections.map((section, i) => {
    if (i !== sectionIdx) return section;
    if (subgroupId === null) return { ...section, items: nextItems };
    return {
      ...section,
      subgroups: (section.subgroups ?? []).map((sg) =>
        sg.id === subgroupId ? { ...sg, items: nextItems } : sg
      ),
    };
  });
}

/**
 * Every item in visual/document order: for each section, its ungrouped items first,
 * then each subgroup's items in subgroup order. Mirrors findItemLocation's traversal.
 */
export function flattenItems(sections: ApiChecklistSection[]): LocatedItem[] {
  const result: LocatedItem[] = [];
  sections.forEach((section, sectionIdx) => {
    for (const item of section.items) {
      result.push({ item, address: { sectionIdx, subgroupId: null } });
    }
    for (const sg of section.subgroups ?? []) {
      for (const item of sg.items) {
        result.push({ item, address: { sectionIdx, subgroupId: sg.id } });
      }
    }
  });
  return result;
}

/**
 * Selected items in visual order. Ids that are no longer present are silently dropped -
 * another member can delete an item over the WebSocket while it is selected.
 */
export function collectSelected(
  sections: ApiChecklistSection[],
  selectedIds: ReadonlySet<string>
): LocatedItem[] {
  return flattenItems(sections).filter((located) => selectedIds.has(located.item.id));
}

/** Ids between two item ids inclusive, in visual order. Returns [] if either id is absent. */
export function idsInRange(
  sections: ApiChecklistSection[],
  anchorId: string,
  focusId: string
): string[] {
  const flat = flattenItems(sections);
  const anchorIdx = flat.findIndex((located) => located.item.id === anchorId);
  const focusIdx = flat.findIndex((located) => located.item.id === focusId);
  if (anchorIdx < 0 || focusIdx < 0) return [];
  const [start, end] =
    anchorIdx <= focusIdx ? [anchorIdx, focusIdx] : [focusIdx, anchorIdx];
  return flat.slice(start, end + 1).map((located) => located.item.id);
}

export type MoveDestination =
  | { kind: "existing"; sectionIdx: number; subgroupId: string | null }
  | { kind: "newSubgroup"; sectionIdx: number; name: string }
  | { kind: "newSection"; name: string };

export type MoveFailure = "empty" | "noop" | "sectionLimit" | "subgroupLimit" | "emptyName";

export type MoveResult =
  | { ok: true; sections: ApiChecklistSection[]; movedCount: number }
  | { ok: false; reason: MoveFailure };

/**
 * Removes selected items from wherever they are, then appends them - in visual order -
 * to the destination container. Ids and checked state are preserved. Emptied containers
 * are left in place. `newSubgroup` / `newSection` create the container and place items in it.
 * Names are normalized with .trim().replace(/\s+/g, " ") before use; duplicate names are allowed.
 * Returns { ok:false, reason:"noop" } when every selected item is already in the destination.
 */
export function moveSelected(
  sections: ApiChecklistSection[],
  selectedIds: ReadonlySet<string>,
  destination: MoveDestination,
  limits: { sectionsMax: number; subgroupsMax: number }
): MoveResult {
  const selected = collectSelected(sections, selectedIds);
  if (selected.length === 0) return { ok: false, reason: "empty" };

  if (destination.kind === "newSection") {
    const name = destination.name.trim().replace(/\s+/g, " ");
    if (!name) return { ok: false, reason: "emptyName" };
    if (sections.length >= limits.sectionsMax) return { ok: false, reason: "sectionLimit" };
  }

  if (destination.kind === "newSubgroup") {
    const name = destination.name.trim().replace(/\s+/g, " ");
    if (!name) return { ok: false, reason: "emptyName" };
    const section = sections[destination.sectionIdx];
    const subgroupCount = section ? (section.subgroups ?? []).length : Infinity;
    if (subgroupCount >= limits.subgroupsMax) return { ok: false, reason: "subgroupLimit" };
  }

  if (destination.kind === "existing") {
    const allInDestination = selected.every(
      (located) =>
        located.address.sectionIdx === destination.sectionIdx &&
        located.address.subgroupId === destination.subgroupId
    );
    if (allInDestination) return { ok: false, reason: "noop" };
  }

  const movedItems = selected.map((located) => located.item);
  let nextSections = removeSelected(sections, selectedIds);

  if (destination.kind === "existing") {
    nextSections = writeContainerItems(
      nextSections,
      destination.sectionIdx,
      destination.subgroupId,
      [
        ...readContainerItems(nextSections, destination.sectionIdx, destination.subgroupId),
        ...movedItems,
      ]
    );
  } else if (destination.kind === "newSubgroup") {
    const name = destination.name.trim().replace(/\s+/g, " ");
    nextSections = nextSections.map((section, i) =>
      i === destination.sectionIdx
        ? {
            ...section,
            subgroups: [
              ...(section.subgroups ?? []),
              { id: crypto.randomUUID(), name, items: movedItems },
            ],
          }
        : section
    );
  } else {
    const name = destination.name.trim().replace(/\s+/g, " ");
    nextSections = [...nextSections, { name, items: movedItems, subgroups: [] }];
  }

  return { ok: true, sections: nextSections, movedCount: movedItems.length };
}

/** Selected items removed from the tree; containers left in place. */
export function removeSelected(
  sections: ApiChecklistSection[],
  selectedIds: ReadonlySet<string>
): ApiChecklistSection[] {
  return sections.map((section) => ({
    ...section,
    items: section.items.filter((it) => !selectedIds.has(it.id)),
    subgroups: (section.subgroups ?? []).map((sg) => ({
      ...sg,
      items: sg.items.filter((it) => !selectedIds.has(it.id)),
    })),
  }));
}

/**
 * Sets `checked` on every selected item. When checking, stamps checkedBy/checkedAt;
 * when unchecking, clears them - matching what applyItemUpdate does for a single toggle.
 */
export function setCheckedForSelected(
  sections: ApiChecklistSection[],
  selectedIds: ReadonlySet<string>,
  checked: boolean,
  by: { sub: string; screenName?: string } | null
): ApiChecklistSection[] {
  function updateItem(item: ApiChecklistItem): ApiChecklistItem {
    if (!selectedIds.has(item.id)) return item;
    const next: ApiChecklistItem = { id: item.id, text: item.text, checked };
    if (checked && by) {
      next.checkedBy = by.sub;
      next.checkedAt = new Date().toISOString();
      if (by.screenName) next.screenName = by.screenName;
    }
    return next;
  }

  return sections.map((section) => ({
    ...section,
    items: section.items.map(updateItem),
    subgroups: (section.subgroups ?? []).map((sg) => ({
      ...sg,
      items: sg.items.map(updateItem),
    })),
  }));
}

export type DestinationRow = {
  /** Stable React key, e.g. `s3` or `s3:sg-abc`. */
  key: string;
  sectionIdx: number;
  subgroupId: string | null;
  sectionName: string;
  /** null for a section's ungrouped area. */
  subgroupName: string | null;
  itemCount: number;
  /** True when the whole current selection already lives in this container. */
  isCurrentHome: boolean;
};

/** Every existing container as a pickable destination row, in visual order. */
export function listDestinations(
  sections: ApiChecklistSection[],
  selectedIds: ReadonlySet<string>
): DestinationRow[] {
  const selected = collectSelected(sections, selectedIds);
  const rows: DestinationRow[] = [];

  function isHome(sectionIdx: number, subgroupId: string | null): boolean {
    if (selected.length === 0) return false;
    return selected.every(
      (located) =>
        located.address.sectionIdx === sectionIdx && located.address.subgroupId === subgroupId
    );
  }

  sections.forEach((section, sectionIdx) => {
    rows.push({
      key: `s${sectionIdx}`,
      sectionIdx,
      subgroupId: null,
      sectionName: section.name,
      subgroupName: null,
      itemCount: section.items.length,
      isCurrentHome: isHome(sectionIdx, null),
    });
    for (const sg of section.subgroups ?? []) {
      rows.push({
        key: `s${sectionIdx}:${sg.id}`,
        sectionIdx,
        subgroupId: sg.id,
        sectionName: section.name,
        subgroupName: sg.name,
        itemCount: sg.items.length,
        isCurrentHome: isHome(sectionIdx, sg.id),
      });
    }
  });

  return rows;
}
